export const ROLES = {
  COMPANY_ADMIN: 'Company Admin',
  MANAGER: 'Manager',
  CASHIER: 'Cashier',
  TECHNICIAN: 'Technician',
  ACCOUNTANT: 'Accountant',
  DELIVERY_PERSON: 'Delivery Person',
} as const;

/** Roles shown when creating/editing team users in the React app. */
export const ASSIGNABLE_ROLES = [ROLES.MANAGER, ROLES.CASHIER, ROLES.DELIVERY_PERSON, ROLES.TECHNICIAN] as const;

/** Display label for company roles in the UI. */
export function roleDisplayLabel(role: string): string {
  if (role === ROLES.TECHNICIAN) return 'Service Team';
  if (role === ROLES.DELIVERY_PERSON) return 'Delivery Person';
  return role;
}

export function isCompanyAdmin(role: string) {
  return role === ROLES.COMPANY_ADMIN;
}

export function isManager(role: string) {
  return role === ROLES.MANAGER;
}

export function isAdminOrManager(role: string) {
  return isCompanyAdmin(role) || isManager(role);
}

/** Non-admin users (Cashier + legacy backend roles) get limited UI. */
export function isCashierLevel(role: string) {
  return !isCompanyAdmin(role) && !isManager(role);
}

export interface CompanyUser {
  name: string;
  user: string;
  full_name: string;
  email: string;
  username?: string;
  mobile?: string;
  role: string;
  is_active: boolean;
  last_login?: string;
  daily_expense_limit?: number;
  shift_expense_limit?: number;
  max_discount_pct?: number;
  can_override_min_price?: boolean;
  can_process_returns?: boolean;
  can_add_expenses?: boolean;
  can_update_item_prices?: boolean;
  can_create_items?: boolean;
  max_refund_amount?: number;
  max_refund_count?: number;
  commission_enabled?: boolean;
  commission_target?: number;
  salary?: number;
}

export interface UserLimits {
  max_users: number | null;
  current_users: number;
  can_add_more: boolean;
}

export interface PackageLimitInfo {
  max: number | null;
  current: number;
  can_add_more: boolean;
}

export interface PackageLimits {
  users: PackageLimitInfo;
  products: PackageLimitInfo;
  invoices: PackageLimitInfo;
}

export interface POSShift {
  name: string;
  cash_pocket: string;
  pocket_name: string;
  cashier?: string;
  cashier_name?: string;
  opening_amount: number;
  shift_start: string;
  shift_end?: string;
  status: 'Open' | 'Closed' | 'Pending Approval';
  closing_amount?: number;
  expected_cash?: number;
  difference?: number;
  total_sales?: number;
  invoice_count?: number;
  cash_sales?: number;
  cash_returns?: number;
  bank_returns?: number;
  payments_collected?: number;
  payments_collected_bank?: number;
  non_cash_breakdown?: Record<string, number>;
  total_drawer_expenses?: number;
  paid_in?: number;
  paid_out?: number;
  drops?: number;
  cash_handover?: number;
  handover_mode?: string;
  handover_to?: string;
  handover_to_name?: string;
  handover_bank?: string;
  z_number?: number | null;
  remarks?: string;
  hours_open?: number;
  max_shift_hours?: number;
  shift_expiry_warning_hours?: number;
  enforce_shift_time_limit?: boolean;
  expires_at?: string | null;
  is_expired?: boolean;
  is_expiring_soon?: boolean;
}

export interface ShiftReturnRow {
  name: string;
  pos_invoice: string;
  refund_amount: number;
  status: string;
}

export interface ShiftInvoiceRow {
  name: string;
  net_total: number;
  status: string;
  paid_amount?: number;
}

export interface ShiftItemRow {
  item: string;
  item_name: string;
  quantity: number;
  amount: number;
}

export interface ShiftCloseReport extends POSShift {
  company_name?: string;
  detail_type: 'invoice' | 'item';
  cash_sales?: number;
  cash_returns?: number;
  bank_returns?: number;
  non_cash_breakdown?: Record<string, number>;
  returns: ShiftReturnRow[];
  total_returns: number;
  return_count: number;
  invoices?: ShiftInvoiceRow[];
  items?: ShiftItemRow[];
  report_type?: 'X' | 'Z';
  reading_note?: string;
  generated_at?: string;
}

export interface DayZReport {
  report_type: 'Z_DAY';
  date: string;
  company_name?: string;
  shift_count: number;
  invoice_count: number;
  return_count: number;
  total_sales: number;
  cash_sales: number;
  cash_returns: number;
  bank_returns: number;
  credit_returns?: number;
  total_drawer_expenses: number;
  paid_in?: number;
  paid_out?: number;
  drops?: number;
  opening_amount: number;
  closing_amount: number;
  expected_cash: number;
  difference: number;
  payments_collected: number;
  payments_collected_bank: number;
  tender_totals: Record<string, number>;
  non_cash_breakdown?: Record<string, number>;
  shifts: POSShift[];
  generated_at?: string;
}

export interface SaaSCompany {
  company_code: string; company_name: string; owner_name: string;
  email: string; mobile: string; city?: string;
  status: 'Pending Trial' | 'Trial Active' | 'Trial Expired' | 'Active' | 'Suspended';
  trial_end?: string; subscription_end?: string; package?: SaaSPackage;
  is_sample_data_loaded: boolean;
  allow_shopify?: boolean;
  allow_whatsapp?: boolean;
  allow_web?: boolean;
  allow_fbr?: boolean;
  allow_ai_analytics?: boolean;
  features?: string[];
  selected_add_ons?: Array<string | { addon_code?: string; name?: string; status?: string; feature_key?: string; amount?: number }>;
}

export interface SaaSPackage {
  package_name: string; package_code: string; monthly_price: number;
  is_custom_price?: boolean | number;
  quarterly_price?: number; annual_price?: number; trial_days: number;
  max_users: number; max_products: number; max_invoices_per_month: number;
  stock_module: boolean; service_module: boolean;
  shopify_module?: boolean;
  allow_shopify?: boolean;
  allow_whatsapp?: boolean;
  reports_level: 'Basic' | 'Advanced'; crm_support: boolean; priority_support: boolean;
  billing_interval?: string;
  cost?: number;
  feature_list?: string[];
  add_ons?: any[];
  display_order?: number;
}

export interface SaaSSubscription {
  name: string;
  saas_company: string;
  package?: string;
  billing_cycle?: string;
  start_date?: string;
  end_date?: string;
  status: 'Pending' | 'Active' | 'Expired' | 'Cancelled';
  amount_paid?: number;
  allow_shopify?: boolean | number;
  allow_whatsapp?: boolean | number;
}

export interface GroupedPackage extends SaaSPackage {
  monthly_package_code?: string;
  quarterly_package_code?: string;
  annual_package_code?: string;
}

export interface User { name: string; full_name: string; email: string; ui_language?: string; }

export interface UserPermissions {
  max_discount_pct?: number;
  can_override_min_price?: boolean;
  can_process_returns?: boolean;
  can_add_expenses?: boolean;
  can_update_item_prices?: boolean;
  can_create_items?: boolean;
  max_refund_amount?: number;
  max_refund_count?: number;
}

export interface AuthState {
  user: User | null; company: { code: string; name: string; status: string } | null;
  role: string; isAuthenticated: boolean; canUsePOS: boolean;
  permissions?: UserPermissions;
}

// POS Item with variants
export interface POSItem {
  name: string; item_code: string; item_name: string; description?: string; category: string;
  item_type: 'Product' | 'Service'; brand?: string; custom_model?: string; sale_price: number; cost_price?: number;
  stock_quantity?: number; barcode?: string; unit_of_measurement?: string;
  has_variants: boolean; variants?: ItemVariant[];
  is_active: boolean | number;
  allow_fractional_qty?: number;
  maintain_stock?: boolean;
  has_stock_ledger?: boolean;
  image?: string;
  custom_fields_data?: Record<string, string>;
  wholesale_price?: number;
  delivery_price?: number;
  reseller_price?: number;
  minimum_price?: number;
  retail_minimum_price?: number;
  /** Percent tax on sale price; used when enable_item_tax is on */
  tax_rate?: number;
  item_tax_template?: string;
  is_weighted?: number | boolean;
  plu_code?: string;
  track_batches?: number | boolean;
  track_serials?: number | boolean;
  has_age_restriction?: boolean | number;
  minimum_age?: number;
  show_on_whatsapp?: number | boolean;
  show_on_shopify?: number | boolean;
  shopify_product_id?: string;
  service_estimated_time?: number | null;
  service_time_unit?: '' | 'Minutes' | 'Hours' | 'Days' | string | null;
  service_staff_required?: number | boolean;
  service_appointment?: 'No Appointment' | 'Optional' | 'Required' | string;
  service_pay_type?: '' | 'Fixed' | 'Percent' | string | null;
  service_pay_value?: number | null;
}

export interface POSServiceJob {
  name: string;
  customer?: string;
  customer_name?: string;
  pos_invoice?: string;
  item?: string;
  item_name?: string;
  quantity?: number;
  rate?: number;
  amount?: number;
  assigned_to?: string;
  assigned_to_name?: string;
  status: 'Pending' | 'In Progress' | 'Completed' | 'Cancelled' | string;
  scheduled_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  appointment_mode?: string;
  estimated_time?: number | null;
  time_unit?: string | null;
  notes?: string;
  creation?: string;
}

export interface POSCustomField {
  name?: string;
  label: string;
  fieldname: string;
  field_type: 'Input' | 'Select';
  options?: string;
  is_unique: boolean | number;
  show_in_pos: boolean | number;
  is_active: boolean | number;
  sort_order: number;
}

export interface ItemVariant {
  name: string;
  variant_name: string;
  price: number;
  cost_price?: number;
  unit_of_measurement?: string;
  parent_item: string;
  stock_quantity?: number;
  barcode?: string;
  maintain_stock?: boolean | number;
  wholesale_price?: number;
  delivery_price?: number;
  reseller_price?: number;
  minimum_price?: number;
  retail_minimum_price?: number;
  has_stock_ledger?: boolean;
  can_set_opening?: boolean;
}

export interface BarcodeScanResult {
  item: string;
  item_code?: string;
  item_name: string;
  item_type: 'Product' | 'Service';
  category?: string;
  variant?: string | null;
  variant_name?: string | null;
  rate: number;
  stock_quantity?: number;
  unit_of_measurement?: string;
  barcode: string;
  wholesale_price?: number;
  delivery_price?: number;
  reseller_price?: number;
  minimum_price?: number;
  retail_minimum_price?: number;
  tax_rate?: number;
  quantity?: number;
  is_weighted?: number | boolean;
  track_batches?: number | boolean;
  track_serials?: number | boolean;
  allow_fractional_qty?: number | boolean;
  has_age_restriction?: boolean | number;
  minimum_age?: number;
  plu_code?: string;
  amount?: number;
  plu_parsed?: boolean;
  weight_source?: string;
  scanned_scale_barcode?: string;
  scale_plu?: string;
  scale_weight?: number;
  serial?: string;
  imei?: string;
  mac_address?: string;
}

export interface ReturnableInvoiceLine {
  invoice_item: string;
  item: string;
  item_name: string;
  item_type: 'Product' | 'Service';
  variant?: string | null;
  variant_name?: string;
  sold_qty: number;
  returned_qty: number;
  returnable_qty: number;
  rate: number;
}

export interface ReturnableInvoice {
  name: string;
  customer?: string;
  customer_name: string;
  invoice_date: string;
  status: string;
  net_total: number;
  paid_amount: number;
  discount_amount: number;
  item_total: number;
  total_refunded: number;
  refundable_cash: number;
  payment_method?: string;
  payment_methods?: Array<{ payment_method: string; amount: number; account?: string }>;
  lines: ReturnableInvoiceLine[];
}

/** @deprecated use ItemVariant */
export type ServiceVariant = ItemVariant;

export interface POSCategory {
  name: string; category_name: string; category_type: 'Product' | 'Service';
  display_order: number; saas_company: string;
  pos_category_icon?: string;
  pos_category_image?: string;
  pos_category_color?: string;
  show_on_storefront?: number | boolean;
  storefront_banner?: string;
  storefront_banner_title?: string;
}

export interface CustomerItemPrice {
  name?: string;
  item: string;
  item_name?: string;
  variant?: string;
  variant_name?: string;
  standard_price?: number;
  custom_price: number;
  custom_delivery_price?: number;
}

export interface POSCustomer {
  name: string; customer_name: string; mobile?: string; email?: string;
  is_walk_in: boolean; outstanding_balance: number; advance_amount?: number;
  invoice_pending?: number; unallocated_outstanding?: number; city?: string;
  address?: string; cnic?: string; ntn?: string; province?: string;
  country?: string; postal_code?: string;
  total_sale?: number;
  paid_amount?: number;
  is_default?: boolean | number;
  customer_type?: 'Retailer' | 'Wholesale' | 'Distributor';
  can_set_opening?: boolean;
  credit_limit?: number;
  loyalty_enrolled?: boolean | number;
  loyalty_points_balance?: number;
  marketing_consent?: boolean | number;
  custom_prices?: CustomerItemPrice[];
}

export interface POSSupplier {
  name: string;
  supplier_name: string;
  mobile?: string;
  email?: string;
  city?: string;
  province?: string;
  country?: string;
  postal_code?: string;
  address?: string;
  contact_person?: string;
  is_active: boolean;
  total_purchase?: number;
  paid_amount?: number;
  outstanding_amount?: number;
  advance_amount?: number;
  last_unit_price?: number;
  last_stock_in_date?: string;
  can_set_opening?: boolean;
}

// Cart Item (can be product or service variant)
export interface CartItem {
  item: string; item_name: string;
  item_code?: string;
  item_type: 'Product' | 'Service';
  quantity: number; rate: number; amount: number;
  category?: string;
  variant?: string; variant_name?: string;
  unit_of_measurement?: string;
  stock_quantity?: number;
  wholesale_price?: number;
  delivery_price?: number;
  reseller_price?: number;
  minimum_price?: number;
  retail_price?: number;
  cost_price?: number;
  customer_custom_price?: number;
  /** Latest Stock In unit_price — rate cannot go below this */
  min_rate?: number;
  /** Percent of line amount when auto item tax is enabled */
  tax_rate?: number;
  is_weighted?: boolean;
  allow_fractional_qty?: boolean;
  has_age_restriction?: boolean | number;
  minimum_age?: number;
  verified_birth_year?: number;
  track_batches?: boolean;
  track_serials?: boolean;
  batch?: string;
  batch_no?: string;
  expiry_date?: string;
  serial?: string;
  imei?: string;
  mac_address?: string;
  service_estimated_time?: number | null;
  service_time_unit?: string | null;
  service_staff_required?: boolean;
  service_appointment?: string;
  service_assigned_to?: string;
  service_scheduled_at?: string;
  service_serve_now?: boolean;
  weight_source?: string;
  scanned_scale_barcode?: string;
  scale_plu?: string;
  scale_weight?: number;
  line_meta?: string;
  /** Packing / kitchen note for this invoice line only */
  line_comment?: string;
}

// Split Payment Entry
export interface PaymentEntry {
  payment_method: string; account: string;
  reference_no: string; amount: number;
}

// POS Invoice
export interface POSInvoice {
  name: string; customer: string; customer_name: string;
  invoice_date: string; status: string;
  item_total: number; service_total: number;
  discount_amount: number; write_off_amount?: number; tax_amount: number;
  net_total: number; paid_amount: number; balance_amount: number;
  previous_outstanding?: number;
  fulfillment_type?: 'Pickup' | 'Delivery';
  delivery_status?: 'Pending' | 'Out for Delivery' | 'Delivered' | 'Cancelled';
  delivery_address?: string;
  delivery_postal_code?: string;
  delivery_recipient_name?: string;
  delivery_phone?: string;
  delivery_notes?: string;
  delivered_at?: string;
  delivery_driver?: string;
  payment_method: string; notes?: string;
  items: CartItem[]; payments: PaymentEntry[];
  fbr_invoice_number?: string;
  fbr_status?: string;
  fbr_qr_code?: string;
  fbr_usin?: string;
  zatca_uuid?: string;
  zatca_status?: string;
  zatca_qr_code?: string;
  zatca_icv?: number;
  zatca_invoice_hash?: string;
}

export interface CashPocket {
  name: string; pocket_name: string;
  current_balance: number; is_default: boolean;
}

export interface BankAccount {
  name: string; bank_name: string; account_title: string;
  account_number?: string; logo?: string | null;
  current_balance: number; is_default: boolean;
  opening_balance?: number;
  can_set_opening?: boolean;
}

export interface DailySummary {
  date: string; total_sales: number; item_sales: number;
  service_sales: number; invoice_count: number;
  payment_breakdown: Record<string, number>;
}

export interface TrialStatus {
  status: string; trial_start?: string; trial_end?: string;
  days_remaining: number; retention_days: number;
  grace_days?: number;
  grace_end?: string;
  data_retention_end?: string;
  in_grace?: boolean;
  grace_days_remaining?: number;
  retention_days_remaining?: number;
  is_sample_data_loaded: boolean;
}

export interface ShiftPerformanceRow {
  cashier: string;
  cashier_name: string;
  shift_count: number;
  closed_shift_count: number;
  total_sales: number;
  invoice_count: number;
  avg_ticket_size: number;
  avg_shift_hours: number;
  avg_variance: number;
  total_variance: number;
  total_drawer_expenses: number;
  return_count: number;
  return_amount: number;
  longest_shift_hours: number;
  expired_open_shifts: number;
  shifts: POSShift[];
}

export interface ShiftPerformanceReport {
  from_date: string;
  to_date: string;
  max_shift_hours: number;
  cashiers: ShiftPerformanceRow[];
}

export interface CommissionRule {
  name?: string;
  rule_name: string;
  is_active: boolean;
  rule_type: 'Flat Percent' | 'Margin Percent' | 'Fixed Per Unit' | 'Tiered Monthly';
  basis: 'Net Sales' | 'Gross Margin' | 'Quantity';
  rate: number;
  fixed_amount?: number;
  apply_scope?: 'All Sales' | 'Category' | 'Products' | 'Services' | 'Specific Items';
  item_category?: string;
  item_category_name?: string;
  item?: string;
  rule_categories?: { item_category: string; category_name?: string }[];
  rule_items?: { item: string; item_name?: string }[];
  priority?: number;
  valid_from?: string;
  valid_to?: string;
  description?: string;
  tiers?: { from_amount: number; to_amount?: number; rate: number }[];
}

export interface CommissionEntry {
  name: string;
  pos_invoice: string;
  pos_invoice_item?: string;
  item?: string;
  item_name?: string;
  earner: string;
  earner_name?: string;
  sales_person?: string;
  sales_person_name?: string;
  rule_name?: string;
  basis_amount?: number;
  commission_rate?: number;
  commission_amount: number;
  status: 'Accrued' | 'Approved' | 'Paid' | 'Reversed';
  creation?: string;
  invoice_datetime?: string;
  invoice_sale_excl_tax?: number;
}

export interface CommissionReport {
  from_date: string;
  to_date: string;
  entries: CommissionEntry[];
  summary: { earner: string; earner_name: string; total_commission: number; entry_count: number }[];
}

// Pagination
export interface PaginatedResponse<T> {
  items?: T[]; customers?: T[]; invoices?: T[];
  total: number; start: number; limit: number;
}

export type PrinterType = 'Network' | 'Windows';
export type PaperWidth = '58mm' | '80mm' | 'A4';

export interface PrinterSettings {
  printer_enabled: boolean;
  printer_type: PrinterType;
  printer_ip: string;
  printer_port: number;
  printer_name: string;
  paper_width: PaperWidth;
  auto_print_receipt: boolean;
}

export interface PosSettings {
  allow_sell_out_of_stock_item: boolean;
  max_discount: number;
  max_round_off: number;
  show_tax_field_in_pos?: boolean;
  show_tax_in_invoice?: boolean;
  /** When true, tax is auto-summed from each cart line's tax_rate % on sale amount */
  enable_item_tax?: boolean;
  /** Label on POS / receipts (Tax, GST, VAT, Sale Tax, …) */
  tax_label?: string;
  show_discount_field_in_pos?: boolean;
  show_discount_in_invoice_print?: boolean;
  print_receipt_after_sale?: boolean;
  show_today_invoices_to_cashier?: boolean;
  pos_screen_layout?: 'Detail Card View' | 'Image Grid View';
  pos_category_position?: 'Top' | 'Bottom' | 'Left' | 'Right';
  pos_touch_mode?: boolean;
  blind_shift_close?: boolean;
  variance_approval_threshold?: number;
  enforce_shift_time_limit?: boolean;
  max_shift_hours?: number;
  shift_expiry_warning_hours?: number;
  enable_commission?: boolean;
  open_drawer_on_cash_payment?: boolean;
  open_drawer_on_cash_movement?: boolean;
  customer_display_enabled?: boolean;
  terminal_provider?: string;
  audit_log_retention_days?: number;
  pos_sync_mode?: 'Live Online' | 'Local First Auto Sync' | 'Offline Always Manual Sync';
  offline_sync_allow_negative_stock?: boolean;
  offline_invoice_prefix?: string;
  offline_bypass_shift_in_local_modes?: boolean;
  use_barcode_or_qr?: 'Barcode' | 'QR Code';
  print_price_on_label?: boolean;
  sell_only_products?: boolean;
  enable_delivery?: boolean;
  delivery_pricing_mode?: 'product_price' | 'runtime_charge';
  receipt_template_settings?: ReceiptTemplateSettings | string;
  daily_sales_summary?: boolean;
  /** Hour of day (0–23) to send daily sales summary */
  daily_sales_summary_hour?: number;
  enable_fbr_integration?: boolean;
  fbr_environment?: 'Sandbox' | 'Production';
  fbr_pos_id?: string;
  fbr_ntn?: string;
  fbr_strn?: string;
  fbr_bearer_token?: string;
  fbr_seller_province?: string;
  fbr_business_category?: string;
  fbr_default_pct_code?: string;
  fbr_fail_safe_mode?: 'Queue & Retry' | 'Strict Block';
  enable_zatca_integration?: boolean;
  zatca_environment?: 'Sandbox' | 'Production';
  zatca_vat_number?: string;
  zatca_company_name_ar?: string;
  zatca_cr_number?: string;
  zatca_fail_safe_mode?: 'Queue & Retry' | 'Strict Block';
  zatca_icv_counter?: number;
  zatca_last_invoice_hash?: string;
  uae_trn?: string;
  uae_company_name_ar?: string;
  uae_asp_provider?: string;
  uae_asp_reference?: string;
  enable_whatsapp_shop?: boolean;
  whatsapp_phone_number_id?: string;
  whatsapp_waba_id?: string;
  whatsapp_catalog_id?: string;
  whatsapp_verify_token?: string;
  whatsapp_business_display_name?: string;
  has_whatsapp_token?: boolean;
  has_whatsapp_app_secret?: boolean;
  enable_shopify?: boolean;
  shopify_shop_domain?: string;
  shopify_api_version?: string;
  shopify_location_id?: string;
  shopify_sync_products_mode?: string;
  shopify_auto_confirm_orders?: boolean;
  shopify_auto_fetch_orders?: boolean;
  shopify_connection_status?: string;
  shopify_last_product_sync_at?: string;
  shopify_last_inventory_sync_at?: string;
  shopify_last_order_sync_at?: string;
  shopify_client_id?: string;
  has_shopify_client_secret?: boolean;
  has_shopify_token?: boolean;
  has_shopify_webhook_secret?: boolean;
  package?: {
    name?: string;
    code?: string;
    package_name?: string;
    package_code?: string;
    shopify_module?: boolean;
    stock_module?: boolean | number;
    service_module?: boolean | number;
  };
  company_name_ar?: string;
  /** Company locale (also on profile) */
  country?: string;
  currency?: string;
  prices_include_tax?: boolean;
  ui_language?: string;
  tax_registration_id?: string;
  privacy_policy_url?: string;
}

export interface ReceiptTemplateSettings {
  header_text?: string;
  footer_text?: string;
  show_logo?: boolean;
  logo_width?: number;
  show_served_by?: boolean;
  show_customer?: boolean;
  show_item_code?: boolean;
  font_size?: 'small' | 'medium' | 'large';
  layout_mode?: 'standard' | 'compact' | 'elegant' | 'short' | 'modern';
  show_email?: boolean;
  show_phone?: boolean;
  show_address?: boolean;
  show_item_price?: boolean;
  show_totals?: boolean;
  show_previous_outstanding?: boolean;
  /** UK invoices only — show VAT Rate (S/Z/R) column from product tax_rate % */
  show_vat_rate?: boolean;
  /** When loyalty is used on the sale — show points earned on the receipt */
  show_loyalty_earned?: boolean;
  /** When loyalty is used on the sale — show new / total points balance */
  show_loyalty_balance?: boolean;
}

export interface WindowsPrinterInfo {
  name: string;
  portName?: string;
  driverName?: string;
  isDefault?: boolean;
}

// Receipt Data
export interface ReceiptData {
  name: string; customer_name: string;
  cashier?: string; cashier_name?: string; company_name?: string;
  company_name_ar?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  company_logo?: string;
  invoice_date: string; creation?: string; status: string;
  item_total: number; service_total: number;
  discount_amount: number; write_off_amount?: number; tax_amount: number;
  promo_total?: number;
  promo_campaigns?: string;
  net_total: number; paid_amount: number;
  balance_amount: number; notes: string;
  previous_outstanding?: number;
  fulfillment_type?: 'Pickup' | 'Delivery';
  delivery_status?: string;
  delivery_address?: string;
  delivery_postal_code?: string;
  delivery_recipient_name?: string;
  delivery_phone?: string;
  delivery_notes?: string;
  delivered_at?: string;
  delivery_driver?: string;
  delivery_charge?: number;
  customer_birth_year?: number;
  items: {
    item_code?: string;
    item_name: string;
    line_comment?: string;
    item_type: string;
    quantity: number;
    rate: number;
    amount: number;
    original_rate?: number;
    promo_amount?: number;
    promo_qty?: number;
    promo_campaign?: string;
    variant_name: string;
    /** POS Item tax_rate % — used for UK VAT Rate (Z/R/S) on receipts */
    tax_rate?: number;
    imei?: string;
    mac_address?: string;
    serial?: string;
    batch_no?: string;
    expiry_date?: string;
    batch?: string;
    is_weighted?: boolean;
    customer_birth_year?: number;
  }[];
  payments: PaymentEntry[];
  fbr_invoice_number?: string;
  fbr_status?: string;
  fbr_qr_code?: string;
  fbr_usin?: string;
  zatca_uuid?: string;
  zatca_status?: string;
  zatca_qr_code?: string;
  zatca_icv?: number;
  zatca_invoice_hash?: string;
  loyalty?: {
    earned?: number;
    redeemed?: number;
    balance?: number;
    loyalty_discount?: number;
  };
}

export interface DeliveryOrder {
  name: string;
  customer: string;
  customer_name: string;
  customer_mobile?: string;
  invoice_date: string;
  invoice_status: string;
  net_total: number;
  paid_amount: number;
  balance_amount: number;
  fulfillment_type: 'Pickup' | 'Delivery';
  delivery_status: 'Pending' | 'Out for Delivery' | 'Delivered' | 'Cancelled';
  delivery_address?: string;
  delivery_postal_code?: string;
  delivery_recipient_name?: string;
  delivery_phone?: string;
  delivery_notes?: string;
  delivered_at?: string;
  delivery_driver?: string;
  cashier?: string;
  creation: string;
  items?: {
    item_name: string;
    quantity: number;
    rate: number;
    amount: number;
    variant_name?: string;
    unit?: string;
  }[];
}

export interface DriverLocation {
  id: string;
  user: string;
  full_name: string;
  latitude: number;
  longitude: number;
  has_location: boolean;
  last_location_update?: string | null;
  minutes_ago?: number | null;
  is_online: boolean;
  active_orders_count: number;
  orders: {
    invoice_name: string;
    customer_name: string;
    phone: string;
    address: string;
    postal_code?: string;
    delivery_status?: string;
  }[];
}

export interface DeliveryMapItem {
  id: string;
  invoice_name: string;
  customer?: string;
  customer_name: string;
  customer_mobile?: string;
  delivery_driver?: string;
  delivery_recipient_name?: string;
  delivery_phone?: string;
  delivery_address?: string;
  delivery_postal_code?: string;
  delivery_status: 'Pending' | 'Out for Delivery' | 'Delivered' | string;
  net_total?: number;
  delivered_at?: string;
  creation: string;
  latitude: number;
  longitude: number;
  has_location?: boolean;
}

export interface POSExpense {
  name?: string;
  saas_company: string;
  posting_date: string;
  recorded_by: string;
  recorded_by_name?: string;
  pos_shift?: string | null;
  cash_pocket?: string | null;
  payment_method: 'Cash' | 'Bank';
  cash_account?: string | null;
  bank_account?: string | null;
  category: string;
  amount: number;
  remarks?: string;
  status: 'Submitted' | 'Cancelled';
  expense_employee?: string;
  cancellation_reason?: string;
  cancelled_by?: string;
  cancelled_by_name?: string;
  cancelled_at?: string;
}

export interface CashMovement {
  name: string;
  movement_type: 'Paid In' | 'Paid Out' | 'Drop' | 'Transfer' | 'Cash Sale' | 'Cash Refund' | string;
  amount: number;
  reason?: string;
  remarks?: string;
  creation: string;
}
